import { makeAutoObservable, runInAction } from "mobx";

import {
  getApiErrorMessage,
  searchDocuments,
  type TeamShort,
  type DocumentSearchResult,
} from "@/api";

import type { RootStore } from "@/store/RootStore";

export type SearchStatus = "idle" | "fetching" | "loaded" | "error";

export type GroupedSearchResult = TeamShort & { documents: DocumentSearchResult[] };

export class SearchStore {
  rootStore: RootStore;

  searchQuery = "";

  status: SearchStatus = "idle";

  error = "";

  documents: DocumentSearchResult[] = [];

  isOpen = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
    if (query.length < 3) {
      this.setStatus("idle");
    } else {
      this.setStatus("fetching");
    }
    this.setError("");
    this.resetDocuments();
  }

  open() {
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
  }

  setStatus(status: SearchStatus) {
    this.status = status;
  }

  setError(error: string) {
    this.error = error;
  }

  resetDocuments() {
    this.documents = [];
  }

  async searchDocs(query: string) {
    if (!query.trim()) {
      this.clearSearch();
      return;
    }

    this.setStatus("fetching");
    this.setError("");

    try {
      const { documents } = await searchDocuments({ query }, { skipCache: true });

      runInAction(() => {
        this.documents = documents ?? [];
      });

      this.setStatus("loaded");
    } catch (err) {
      console.error("Search failed:", err);
      this.setError(getApiErrorMessage(err));
      this.setStatus("error");
      this.resetDocuments();
    }
  }

  clearSearch() {
    this.searchQuery = "";
    this.status = "idle";
    this.documents = [];
  }

  get groupedDocs(): GroupedSearchResult[] {
    if (!this.documents.length) {
      return [];
    }

    const grouped = new Map<string, GroupedSearchResult>();

    this.documents.forEach((doc) => {
      if (doc.team) {
        const teamKey = doc.team.uid;
        if (!grouped.has(teamKey)) {
          grouped.set(teamKey, {
            ...doc.team,
            documents: [],
          });
        }
        grouped.get(teamKey)!.documents.push(doc);
      }
    });

    return Array.from(grouped.values());
  }
}
